import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { RoleBadge } from "@/components/members/RoleBadge";
import { useToast } from "@/hooks/use-toast";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { formatDistanceToNow } from "date-fns";
import { Send, MessageSquare, Edit, Trash2, AtSign } from "lucide-react";
import { MemberRole } from "@/lib/permissions";

interface Comment {
  id: string;
  task_id: string;
  user_id: string;
  comment: string;
  created_at: string;
  edited_at?: string;
  mentioned_user_ids?: string[];
  commenter?: {
    full_name: string;
    email: string;
  };
  member_role?: MemberRole;
}

interface UnaMember {
  id: string;
  full_name: string;
  member_role: MemberRole;
  user_id: string;
}

interface TaskCommentsProps {
  taskId: string;
  unaId: string;
  subtaskId?: string;
}

export function TaskComments({ taskId, unaId, subtaskId }: TaskCommentsProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [deleteCommentId, setDeleteCommentId] = useState<string | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionedUsers, setMentionedUsers] = useState<UnaMember[]>([]);
  const [availableMembers, setAvailableMembers] = useState<UnaMember[]>([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (taskId && unaId) {
      fetchComments();
      fetchUnaMembers();
      const unsubscribe = subscribeToComments();
      return () => {
        unsubscribe?.();
      };
    }
  }, [taskId, unaId, subtaskId]);

  const fetchComments = async () => {
    setLoading(true);
    let query = supabase
      .from("task_comments")
      .select("*")
      .eq("task_id", taskId)
      .eq("una_id", unaId)
      .order("created_at", { ascending: true });

    if (subtaskId) {
      query = query.eq('subtask_id', subtaskId);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching comments:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load comments",
      });
    } else {
      const commentsWithDetails = await Promise.all(
        (data || []).map(async (comment) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("full_name, email")
            .eq("id", comment.user_id)
            .single();

          const { data: memberData } = await supabase
            .from("una_members")
            .select("member_role")
            .eq("una_id", unaId)
            .eq("user_id", comment.user_id)
            .single();

          return {
            ...comment,
            mentioned_user_ids: Array.isArray(comment.mentioned_user_ids) 
              ? comment.mentioned_user_ids as string[]
              : [],
            commenter: {
              full_name: profile?.full_name || "Unknown",
              email: profile?.email || "",
            },
            member_role: memberData?.member_role as MemberRole,
          };
        })
      );
      setComments(commentsWithDetails);
    }
    setLoading(false);
  };

  const fetchUnaMembers = async () => {
    const { data } = await supabase
      .from('una_members')
      .select('id, full_name, member_role, user_id')
      .eq('una_id', unaId)
      .eq('status', 'active');
    
    if (data) {
      setAvailableMembers(data as UnaMember[]);
    }
  };

  const subscribeToComments = () => {
    const channel = supabase
      .channel(`task_comments:${taskId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "task_comments",
          filter: `task_id=eq.${taskId}`,
        },
        () => {
          fetchComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return;

    setSubmitting(true);
    const mentionedUserIds = mentionedUsers.map(m => m.user_id);
    
    const { error } = await supabase.from("task_comments").insert({
      task_id: taskId,
      user_id: user.id,
      una_id: unaId,
      comment: newComment.trim(),
      mentioned_user_ids: mentionedUserIds.length > 0 ? mentionedUserIds : null,
      subtask_id: subtaskId || null,
    });

    if (error) {
      console.error("Error adding comment:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add comment",
      });
    } else {
      // Send mention notifications
      if (mentionedUserIds.length > 0) {
        try {
          await supabase.functions.invoke('send-comment-mention-notification', {
            body: {
              taskId,
              commentText: newComment.trim(),
              mentionedUserIds,
              commenterId: user.id,
            },
          });
        } catch (err) {
          console.error('Error sending mention notifications:', err);
        }
      }

      setNewComment("");
      setMentionedUsers([]);
      toast({
        title: "Success",
        description: mentionedUserIds.length > 0 
          ? `Comment posted and ${mentionedUserIds.length} ${mentionedUserIds.length === 1 ? 'person' : 'people'} notified`
          : "Comment added successfully",
      });
    }
    setSubmitting(false);
  };

  const handleEditComment = async (commentId: string) => {
    if (!editText.trim()) return;

    const { error } = await supabase
      .from("task_comments")
      .update({
        comment: editText.trim(),
        edited_at: new Date().toISOString(),
      })
      .eq("id", commentId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update comment",
      });
    } else {
      setEditingCommentId(null);
      setEditText("");
      toast({
        title: "Success",
        description: "Comment updated",
      });
    }
  };

  const handleDeleteComment = async () => {
    if (!deleteCommentId) return;

    const { error } = await supabase
      .from("task_comments")
      .delete()
      .eq("id", deleteCommentId);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete comment",
      });
    } else {
      setDeleteCommentId(null);
      toast({
        title: "Success",
        description: "Comment deleted",
      });
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    setNewComment(value);
    setCursorPosition(cursorPos);

    // Check for @ mention trigger
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt);
        setMentionOpen(true);
      }
    } else {
      setMentionOpen(false);
    }
  };

  const handleMentionSelect = (member: UnaMember) => {
    const textBeforeCursor = newComment.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const textAfterCursor = newComment.substring(cursorPosition);
    
    const newText = 
      newComment.substring(0, lastAtIndex) + 
      `@${member.full_name} ` + 
      textAfterCursor;
    
    setNewComment(newText);
    setMentionedUsers(prev => [...prev, member]);
    setMentionOpen(false);
    setMentionSearch("");
    
    // Focus back on textarea
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const removeMention = (memberId: string) => {
    setMentionedUsers(prev => prev.filter(m => m.id !== memberId));
  };

  const filteredMembers = availableMembers.filter(member =>
    member.full_name.toLowerCase().includes(mentionSearch.toLowerCase())
  );

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5" />
        <h3 className="text-lg font-semibold">Comments</h3>
        <Badge variant="secondary">{comments.length}</Badge>
      </div>

      {/* Comments List */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {comments.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No comments yet. Be the first to comment!
          </p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id}>
              {editingCommentId === comment.id ? (
                // Edit Mode
                <div className="flex gap-3 p-3 rounded-lg border bg-background">
                  <Avatar className="h-8 w-8 mt-1">
                    <AvatarFallback className="text-xs">
                      {getInitials(comment.commenter?.full_name || "?")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-2">
                    <Textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={3}
                      className="resize-none"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingCommentId(null);
                          setEditText("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleEditComment(comment.id)}
                        disabled={!editText.trim()}
                      >
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div className="flex gap-3 p-3 rounded-lg bg-muted/50">
                  <Avatar className="h-8 w-8 mt-1">
                    <AvatarFallback className="text-xs">
                      {getInitials(comment.commenter?.full_name || "?")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">
                        {comment.commenter?.full_name || "Unknown User"}
                      </span>
                      {comment.member_role && (
                        <RoleBadge role={comment.member_role} size="small" abbreviated />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(comment.created_at), {
                          addSuffix: true,
                        })}
                      </span>
                      {comment.edited_at && (
                        <Badge variant="secondary" className="text-xs">
                          Edited
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap break-words">{comment.comment}</p>
                    {user?.id === comment.user_id && (
                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingCommentId(comment.id);
                            setEditText(comment.comment);
                          }}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => setDeleteCommentId(comment.id)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Comment */}
      <div className="space-y-2">
        {mentionedUsers.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {mentionedUsers.map((member) => (
              <Badge key={member.id} variant="secondary" className="gap-1">
                <AtSign className="h-3 w-3" />
                {member.full_name}
                <button
                  onClick={() => removeMention(member.id)}
                  className="ml-1 hover:text-destructive"
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>
        )}
        <Popover open={mentionOpen} onOpenChange={setMentionOpen}>
          <PopoverTrigger asChild>
            <div className="relative">
              <Textarea
                ref={textareaRef}
                placeholder="Add a comment... Use @ to mention team members"
                value={newComment}
                onChange={handleTextareaChange}
                rows={3}
                className="resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    handleAddComment();
                  }
                }}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-64" align="start">
            <Command>
              <CommandInput placeholder="Search members..." value={mentionSearch} />
              <CommandList>
                <CommandEmpty>No members found.</CommandEmpty>
                <CommandGroup>
                  {filteredMembers.map((member) => (
                    <CommandItem
                      key={member.id}
                      onSelect={() => handleMentionSelect(member)}
                    >
                      <div className="flex items-center gap-2">
                        <Avatar className="h-6 w-6">
                          <AvatarFallback className="text-xs">
                            {getInitials(member.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">{member.full_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {member.member_role}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {newComment.length} / 2000 characters
          </span>
          <Button
            onClick={handleAddComment}
            disabled={!newComment.trim() || submitting || newComment.length > 2000}
            size="sm"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? "Posting..." : "Post Comment"}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteCommentId} onOpenChange={() => setDeleteCommentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment?</AlertDialogTitle>
            <AlertDialogDescription>
              This comment will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteComment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Comment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
